import React from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from './cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and disables the button. */
  loading?: boolean;
  /** Icon rendered before the label. */
  icon?: React.ReactNode;
  /** Stretch to fill the container width. */
  fullWidth?: boolean;
}

const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-brand hover:bg-brand-dark text-white border border-transparent shadow-lg',
  secondary:
    'bg-[var(--bg-surface-alt)] hover:bg-[var(--bg-surface)] text-[var(--text-primary)] border border-[var(--border-secondary)]',
  ghost: 'bg-transparent hover:bg-[var(--bg-surface-alt)] text-[var(--text-secondary)] border border-transparent',
  danger: 'bg-red-600 hover:bg-red-500 text-white border border-transparent shadow-lg',
};

// md/lg keep a 44px min touch target; sm (36px) is for dense desktop controls only.
const SIZES: Record<ButtonSize, string> = {
  sm: 'min-h-[36px] px-3 text-tiny gap-1.5',
  md: 'min-h-[44px] px-4 text-xs gap-2',
  lg: 'min-h-[44px] px-5 text-sm gap-2',
};

/**
 * Standard button. Replaces the ~465 hand-rolled rounded-full button recipes
 * scattered across the app with one consistent, accessible primitive.
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, icon, fullWidth, disabled, className, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center rounded-pill font-black uppercase tracking-widest',
        'transition active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60',
        'disabled:opacity-50 disabled:pointer-events-none',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Loader2 size={16} className="animate-spin" /> : icon}
      {children}
    </button>
  );
});
